import React from 'react';
import { useQuery } from '@apollo/client';
import { Link } from 'react-router-dom';

import {
    IconBinoculars,
    IconBroadcast,
    IconChartLine1,
    IconClipboardCheck,
    IconGapAnalysis,
    IconMediaLibrary1,
    TrqkLogo,
    LeftNavItem,
    LeftNavLayout,
    TrqkIQLogo,
} from '@trqk/components';
import { queryStrings } from '@trqk/resolvers';
import { getEnvironment, getEnableControlPanel, locale } from '@trqk/utils';

import { ManageDetectionsButton, ManageStatementsButton } from '../../nav';
import { MAIN_CONTENT_ID, PATHS, FULL_PATHS } from '../../../constants';
import { AccountDropdown } from '../../menus';
import { usePaymentPlanStatus } from '../../../hooks';
import { FreeTrialFooter } from '.';

import './web-layout.css';

const { GET_SESSION_IS_VALID } = queryStrings;

const enableControlPanel = getEnableControlPanel();

/**
 * An instance of LeftNavLayout that includes the nav menu items and account dropdown menu for the
 * @trqk/web app.
 * @param {LeftNavLayoutInstanceProps} props
 */
export const WebLayout = ({
    children,
    collapsedHeaderPadding,
    header,
    headerPadding,
    mainWidth,
    loading,
    topPadding,
}: LeftNavLayoutInstanceProps): JSX.Element | null => {
    const environment = getEnvironment();
    const { data: sessionData } = useQuery(GET_SESSION_IS_VALID);
    const sessionIsValid = sessionData?.session?.sessionIsValid ?? false;
    const { isPublisherEdition, showFreeTrialFooter } = usePaymentPlanStatus();

    return (
        <LeftNavLayout
            // props passed through to LeftNavLayout
            collapsedHeaderPadding={collapsedHeaderPadding}
            footer={
                showFreeTrialFooter
                    ? {
                          content: <FreeTrialFooter />,
                      }
                    : null
            }
            header={header}
            headerPadding={headerPadding}
            mainWidth={mainWidth}
            loading={loading}
            logo={<TrqkLogo width={110} />}
            topPadding={topPadding}
            // props specifically for @trqk/web instance of LeftNavLayout
            accountDropdown={<AccountDropdown />}
            mainContentId={MAIN_CONTENT_ID}
            menuItems={
                <>
                    <div className="ml-4 border-r border-tertiary-200">
                        <TrqkIQLogo
                            isPublisherEdition={isPublisherEdition}
                            width={100}
                        />
                    </div>
                    <LeftNavItem
                        disabled={!sessionIsValid}
                        path={PATHS.INSIGHTS}
                        linkTo={location => ({
                            search: location.search,
                            pathname: PATHS.INSIGHTS,
                        })}
                        Icon={IconBinoculars}
                        label={locale.enUS.NAV_MENU.LABELS.INSIGHTS}
                        Link={Link}
                    />
                    {sessionIsValid && (
                        <LeftNavItem
                            path={PATHS.STATEMENTS}
                            linkTo={location => ({
                                search: location.search,
                                pathname: `${PATHS.STATEMENTS}/charts`,
                            })}
                            Icon={IconChartLine1}
                            label={locale.enUS.NAV_MENU.LABELS.STATEMENTS}
                            Link={Link}
                        >
                            <ManageStatementsButton>
                                {locale.enUS.NAV_MENU.LABELS.MANAGE_ADD}
                            </ManageStatementsButton>
                        </LeftNavItem>
                    )}
                    {!sessionIsValid && (
                        <LeftNavItem
                            path={PATHS.STATEMENTS_PREVIEW}
                            linkTo={location => ({
                                search: location.search,
                                pathname: PATHS.STATEMENTS_PREVIEW,
                            })}
                            Icon={IconChartLine1}
                            label={locale.enUS.NAV_MENU.LABELS.STATEMENTS}
                            Link={Link}
                        />
                    )}
                    <LeftNavItem
                        disabled={!sessionIsValid}
                        path={PATHS.WORKS}
                        linkTo={location => ({
                            search: location.search,
                            pathname: PATHS.WORKS,
                        })}
                        Icon={IconMediaLibrary1}
                        label={locale.enUS.NAV_MENU.LABELS.WORKS}
                        Link={Link}
                    />
                    {!isPublisherEdition && (
                        <LeftNavItem
                            path={PATHS.DETECTIONS}
                            linkTo={location => ({
                                search: location.search,
                                pathname: PATHS.DETECTIONS,
                            })}
                            disabled={!sessionIsValid}
                            Icon={IconBroadcast}
                            label={locale.enUS.NAV_MENU.LABELS.DETECTIONS}
                            Link={Link}
                        >
                            <ManageDetectionsButton>
                                {locale.enUS.NAV_MENU.LABELS.MANAGE_ADD}
                            </ManageDetectionsButton>
                        </LeftNavItem>
                    )}
                    {isPublisherEdition && (
                        <LeftNavItem
                            disabled={!sessionIsValid}
                            path={PATHS.GAP_ANALYSIS}
                            linkTo={location => ({
                                search: location.search,
                                pathname: PATHS.GAP_ANALYSIS,
                            })}
                            Icon={IconGapAnalysis}
                            label={locale.enUS.NAV_MENU.LABELS.GAP_ANALYSIS}
                            Link={Link}
                        >
                            <Link
                                to={({ search }) => ({
                                    search,
                                    pathname: FULL_PATHS.MANAGE_GAP_ANALYSIS,
                                    state: {
                                        // This is used by the AnchorLinksProvider to disable scrolling to anchor links
                                        // when the Create Gap Analysis modal is open
                                        noAnchorScroll: true,
                                    },
                                })}
                                className="text-sm underline text-tertiary-400 hover:text-primary-500"
                            >
                                {locale.enUS.NAV_MENU.LABELS.CREATE}
                            </Link>
                        </LeftNavItem>
                    )}
                    <LeftNavItem
                        disabled={!sessionIsValid}
                        path={PATHS.REPORTS}
                        linkTo={location => ({
                            search: location.search,
                            pathname: PATHS.REPORTS,
                        })}
                        Icon={IconClipboardCheck}
                        label={locale.enUS.NAV_MENU.LABELS.REPORTS}
                        Link={Link}
                    />
                </>
            }
        >
            {enableControlPanel && (
                <div
                    className="fixed"
                    style={{
                        bottom: showFreeTrialFooter ? 66 : 10,
                        right: 20,
                        zIndex: 200,
                    }}
                >
                    <div className="px-2 py-1 text-sm border rounded border-quinary-500">
                        <code className="text-quinary-500">
                            env: {environment}
                        </code>
                    </div>
                </div>
            )}
            {children}
        </LeftNavLayout>
    );
};
